<template>
  <div class="major-management-container">
    <div class="steps-indicator">
      <div :class="{ 'step-active': currentStep === 1, 'step-completed': currentStep > 1 }">
        <span>1</span> 专业基本信息
      </div>
      <div :class="{ 'step-active': currentStep === 2, 'step-completed': currentStep > 2 }">
        <span>2</span> 考试科目设置
      </div>
      <div :class="{ 'step-active': currentStep === 3, 'step-completed': currentStep > 3 }">
        <span>3</span> 关联学校设置
      </div>
    </div>
    <!-- 步骤1: 专业基本信息 -->
    <div v-if="currentStep === 1" class="step-content">
      <h3>专业基本信息</h3>
      <a-form ref="basicForm" :form="form" :model="basicInfo" :rules="rules" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-row :gutter="20">
          <a-col :span="12">
            <a-form-item label="选择省份" name="province_id" required>
              <a-select 
                :value="basicInfo.province_id" 
                @change="handleProvinceChange" 
                placeholder="请先选择省份"
                :disabled="!!$route.query.id"
              >
                <a-select-option 
                  v-for="province in provinces" 
                  :key="province.id" 
                  :value="province.id"
                >
                  {{ province.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="选择学历" name="education">
              <a-radio-group :value="basicInfo.education" @change="(e) => basicInfo.education = e.target.value">
                <a-radio value="专科">专科</a-radio>
                <a-radio value="专升本">专升本</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="20">
          <a-col :span="12">
            <a-form-item label="专业名称" name="name">
              <a-input :value="basicInfo.name" @input="basicInfo.name = $event.target.value" placeholder="请输入专业名称" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="专业代码" name="code">
              <a-input :value="basicInfo.code" @input="basicInfo.code = $event.target.value" placeholder="请输入专业代码" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="20">
          <a-col :span="12">
            <a-form-item label="学分要求" name="credits">
              <a-input :value="basicInfo.credits" @input="basicInfo.credits = $event.target.value" type="number" placeholder="请输入学分要求" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="课程门数" name="course_count">
              <a-input :value="basicInfo.course_count" @input="basicInfo.course_count = $event.target.value" type="number" placeholder="请输入课程门数" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="20">
          <a-col :span="12">
            <a-divider>政策说明</a-divider>
            <Tinymce v-model="basicInfo.policy_html" :height="300" />
          </a-col>
        </a-row>
      </a-form>
      <div class="step-buttons">
        <a-button type="primary" @click="nextStep">下一步</a-button>
      </div>
    </div>

    <!-- 步骤2: 考试科目设置 -->
    <div v-if="currentStep === 2" class="step-content">
      <h3>考试科目设置</h3>
      <a-button type="primary" size="small" @click="addSubject" style="margin-bottom: 20px;">添加科目</a-button>
      <div v-if="subjects.length > 0">
        <a-table :data-source="subjects" rowKey="id" bordered style="width: 100%">
          <a-table-column dataIndex="id" title="序号" width="80">
            <template slot-scope="text, record, index">
              {{ index + 1 }}
            </template>
          </a-table-column>
          <a-table-column dataIndex="code" title="课程代码" width="200">
            <template slot-scope="text, record">
              <a-input 
                v-model="record.code" 
                placeholder="请输入课程代码" 
                @change="(e) => handleSubjectChange(record.id, 'code', e.target.value)"
              />
            </template>
          </a-table-column>
          <a-table-column dataIndex="name" title="科目名称" width="200">
            <template slot-scope="text, record">
              <a-input 
                v-model="record.name" 
                placeholder="请输入科目名称"
                @change="(e) => handleSubjectChange(record.id, 'name', e.target.value)"
              />
            </template>
          </a-table-column>
          <a-table-column dataIndex="credits" title="学分" width="150">
            <template slot-scope="text, record">
              <a-input-number 
                v-model="record.credits" 
                :min="1" 
                style="width: 100%" 
                placeholder="学分"
                @change="(value) => handleSubjectChange(record.id, 'credits', value)"
              />
            </template>
          </a-table-column>
          <a-table-column dataIndex="type" title="类型" width="150">
            <template slot-scope="text, record">
              <a-select 
                :value="record.type" 
                placeholder="请选择" 
                style="width: 100%"
                @change="(value) => handleSubjectChange(record.id, 'type', value)"
              >
                <a-select-option value="required">必考</a-select-option>
                <a-select-option value="optional">选考</a-select-option>
              </a-select>
            </template>
          </a-table-column>
          <a-table-column dataIndex="is_show" title="显示" width="150">
            <template slot-scope="text, record">
              <a-select 
                :value="record.is_show" 
                placeholder="请选择" 
                style="width: 100%"
                @change="(value) => handleSubjectChange(record.id, 'is_show', value)"
              >
                <a-select-option :value="1">显示</a-select-option>
                <a-select-option :value="0">不显示</a-select-option>
              </a-select>
            </template>
          </a-table-column>
          <a-table-column title="考期设置" width="400">
            <template slot-scope="text, record">
              <!-- 确保 examSchedules 始终存在且是数组 -->
              <div v-if="!record.examSchedules || record.examSchedules.length === 0" class="exam-schedule-item">
                <div style="display: flex; align-items: center; margin-bottom: 10px;">
                  <a-button 
                    type="primary" 
                    size="small" 
                    @click="addSchedule(record.id)"
                  >
                    添加考期
                  </a-button>
                </div>
              </div>
              <div v-for="(schedule, index) in (record.examSchedules || [])" :key="schedule.id || index" class="exam-schedule-item">
                <div style="display: flex; align-items: center; margin-bottom: 10px;">
                  <a-select 
                    :value="schedule.month" 
                    placeholder="考期" 
                    style="width: 100px; margin-right: 10px;"
                    @change="(value) => handleScheduleChange(record.id, index, 'month', value)"
                  >
                    <a-select-option value="一月">一月</a-select-option>
                    <a-select-option value="四月">四月</a-select-option>
                    <a-select-option value="七月">七月</a-select-option>
                    <a-select-option value="十月">十月</a-select-option>
                  </a-select>
                  <a-select 
                    :value="schedule.day" 
                    placeholder="周几" 
                    style="width: 100px; margin-right: 10px;"
                    @change="(value) => handleScheduleChange(record.id, index, 'day', value)"
                  >
                    <a-select-option value="周六">周六</a-select-option>
                    <a-select-option value="周日">周日</a-select-option>
                  </a-select>
                  <a-select 
                    :value="schedule.time" 
                    placeholder="时间" 
                    style="width: 100px; margin-right: 10px;"
                    @change="(value) => handleScheduleChange(record.id, index, 'time', value)"
                  >
                    <a-select-option value="上午">上午</a-select-option>
                    <a-select-option value="下午">下午</a-select-option>
                  </a-select>
                  <a-input 
                    :value="schedule.remark" 
                    placeholder="备注" 
                    style="width: 200px; margin-right: 10px;"
                    @change="(e) => handleScheduleChange(record.id, index, 'remark', e.target.value)"
                  />
                  <a-button 
                    type="primary" 
                    size="small" 
                    @click="addSchedule(record.id)" 
                    style="margin-right: 10px;"
                  >
                    添加
                  </a-button>
                  <a-button 
                    type="danger" 
                    size="small" 
                    @click="deleteSchedule(record.id, index)"
                    v-if="(record.examSchedules || []).length > 1"
                  >
                    删除
                  </a-button>
                </div>
              </div>
            </template>
          </a-table-column>
          <a-table-column title="操作" width="100" fixed="right">
            <template slot-scope="record">
              <a-button type="danger" size="small" @click="deleteSubject(record.id)">删除</a-button>
            </template>
          </a-table-column>
        </a-table>
      </div>
      <div v-else class="no-data-tip" style="text-align: center; padding: 20px; color: #999;">
        暂无科目数据，请点击"添加科目"按钮添加
      </div>
      <div class="step-buttons">
        <a-button @click="prevStep">上一步</a-button>
        <a-button type="primary" @click="nextStep">下一步</a-button>
      </div>
    </div>

    <!-- 步骤3: 关联学校设置 -->
    <div v-if="currentStep === 3" class="step-content">
      <h3>关联学校设置</h3>
      <a-transfer
        :data-source="schools"
        :titles="['可选学校', '已选学校']"
        :target-keys="selectedSchools"
        :render="renderSchool"
        @change="handleSchoolChange"
        :list-style="{
          width: '45%',
          height: '400px',
        }"
      ></a-transfer>

      <div v-if="selectedSchools.length > 0" class="selected-schools" style="margin-top: 20px">
        <h4>已选学校列表</h4>
        <a-table :data-source="selectedSchoolsData" :pagination="false" bordered style="width: 100%">
          <a-table-column key="name" title="学校名称" data-index="name"></a-table-column>
          <a-table-column key="isMajor" dataIndex="isMajor" title="是否主修专业" width="150">
            <template slot-scope="text, record">
              <a-radio-group 
                :value="record.isMajor ? 'yes' : 'no'"
                @change="(e) => handleMajorChange(record.key, e.target.value === 'yes')"
                button-style="solid"
              >
                <a-radio-button value="yes">是</a-radio-button>
                <a-radio-button value="no">否</a-radio-button>
              </a-radio-group>
            </template>
          </a-table-column>
        </a-table>
      </div>

      <div class="step-buttons" style="margin-top: 20px">
        <a-button @click="prevStep">上一步</a-button>
        <a-button type="primary" @click="handleSubmit" :loading="submitting">提交</a-button>
      </div>
    </div>

     <div style="margin-top: 30px;margin-bottom: 10px; display: flex; align-items: center;">
        <span style="margin-right: 10px;">临时保存：</span>
        <a-switch v-model="enableTemporarySave" checked-children="开启" un-checked-children="关闭" />
      </div>
  </div>
</template>

<script>
import { message } from 'ant-design-vue';
import { addOrUpdateMajor, getMajorDetail, getUniversityList } from '@/libs/analysis';
import Tinymce from '@/components/Tinymce'

export default {
  name: 'MajorManagement',
  components: { Tinymce },
  data() {
    return {
      // 步骤控制
      currentStep: 1,
      submitting: false,
      form: this.$form.createForm(this),
      // 临时保存开关状态
      enableTemporarySave: false,
      // 基础信息
      basicInfo: {
        id: undefined,
        name: '',
        code: '',
        province_id: '',
        province_name: '',
        education: '专科',
        credits: 0,
        course_count: 0,
        policy_html: ''
      },
      // 表单验证规则
      rules: {
        province_id: [{ required: true, message: '请选择省份', trigger: 'change' }],
        name: [{ required: true, message: '请输入专业名称', trigger: 'blur' }],
        code: [{ required: true, message: '请输入专业代码', trigger: 'blur' }],
        credits: [
          { required: true, message: '请输入学分要求', trigger: 'blur' },
          { type: 'number', min: 1, message: '学分必须大于0', trigger: 'blur' }
        ],
        course_count: [
          { required: true, message: '请输入课程门数', trigger: 'blur' },
          { type: 'number', min: 1, message: '课程门数必须大于0', trigger: 'blur' }
        ]
      },
      // 科目相关
      subjects: [],
      // 学校相关
      schools: [],
      selectedSchools: [],
      selectedSchoolsData: [],
      provinces: [
        { id: '110000', name: '北京市' },
        { id: '120000', name: '天津市' },
        { id: '130000', name: '河北省' },
        { id: '140000', name: '山西省' },
        { id: '150000', name: '内蒙古自治区' },
        { id: '210000', name: '辽宁省' },
        { id: '220000', name: '吉林省' },
        { id: '230000', name: '黑龙江省' },
        { id: '310000', name: '上海市' },
        { id: '320000', name: '江苏省' },
        { id: '330000', name: '浙江省' },
        { id: '340000', name: '安徽省' },
        { id: '350000', name: '福建省' },
        { id: '360000', name: '江西省' },
        { id: '370000', name: '山东省' },
        { id: '410000', name: '河南省' },
        { id: '420000', name: '湖北省' },
        { id: '430000', name: '湖南省' },
        { id: '440000', name: '广东省' },
        { id: '450000', name: '广西壮族自治区' },
        { id: '460000', name: '海南省' },
        { id: '500000', name: '重庆市' },
        { id: '510000', name: '四川省' },
        { id: '520000', name: '贵州省' },
        { id: '530000', name: '云南省' },
        { id: '540000', name: '西藏自治区' },
        { id: '610000', name: '陕西省' },
        { id: '620000', name: '甘肃省' },
        { id: '630000', name: '青海省' },
        { id: '640000', name: '宁夏回族自治区' },
        { id: '650000', name: '新疆维吾尔自治区' },
        { id: '710000', name: '台湾省' },
        { id: '810000', name: '香港特别行政区' },
        { id: '820000', name: '澳门特别行政区' }
      ]
    }
  },
  methods: {
    // 临时保存数据到localStorage
    saveTemporaryData(showMessage = false) {
      try {
        const temporaryData = {
          basicInfo: this.basicInfo,
          subjects: this.subjects,
          selectedSchools: this.selectedSchools,
          selectedSchoolsData: this.selectedSchoolsData,
          currentStep: this.currentStep,
          timestamp: Date.now()
        };
        localStorage.setItem('majorManagementTemporaryData', JSON.stringify(temporaryData));
        if (showMessage) {
          message.success('数据已临时保存');
        }
      } catch (error) {
        console.error('临时保存失败:', error);
        message.error('临时保存失败，请重试');
      }
    },
    
    // 从localStorage加载临时数据
    loadTemporaryData() {
      try {
        const storedData = localStorage.getItem('majorManagementTemporaryData');
        if (storedData) {
          const temporaryData = JSON.parse(storedData);
          // 检查是否需要加载临时数据（只有在不是编辑现有专业的情况下）
          if (!this.$route.query.id) {
            // 询问用户是否恢复临时数据
            this.$confirm({
              title: '提示',
              content: '检测到您有未保存的数据，是否恢复？',
              onOk: () => {
                this.basicInfo = { ...temporaryData.basicInfo };
                this.subjects = [...temporaryData.subjects];
                this.selectedSchools = [...temporaryData.selectedSchools];
                this.selectedSchoolsData = [...temporaryData.selectedSchoolsData];
                this.currentStep = temporaryData.currentStep;
                
                // 如果有省份信息，需要重新加载学校列表
                if (this.basicInfo.province_id) {
                  this.fetchUniversityList(this.basicInfo.province_id);
                }
                
                message.success('数据已恢复');
              }
            });
          }
        }
      } catch (error) {
        console.error('加载临时数据失败:', error);
        // 不显示错误消息，避免影响用户体验
      }
    },
    
    // 清除临时数据
    clearTemporaryData() {
      try {
        localStorage.removeItem('majorManagementTemporaryData');
      } catch (error) {
        console.error('清除临时数据失败:', error);
      }
    },
    
    // 获取学校列表
    async fetchUniversityList(provinceId) {
      if (!provinceId) {
        this.schools = [];
        return;
      }
      
      try {
        const res = await getUniversityList({ province_id: provinceId });
        console.log('学校列表:', res);
        if (res.total > 0) {
          this.schools = res.list.map(school => ({
            ...school,
            key: school.id,
            title: school.name,
            isMajor: false
          })) || [];
        } else {
          message.error(res.msg || '该省份下暂无学校数据');
          this.schools = [];
        }
      } catch (error) {
        console.error('获取学校列表失败:', error);
        message.error('获取学校列表失败');
        this.schools = [];
      }
    },
    // 处理省份变更
    handleProvinceChange(provinceId) {
      this.basicInfo.province_id = provinceId;
      this.basicInfo.province_name = this.provinces.find(p => p.id === provinceId)?.name;
      this.fetchUniversityList(provinceId);
      // 清空已选的学校
      this.basicInfo.university = undefined;
      this.selectedSchools = [];
      this.selectedSchoolsData = [];
      // this.$refs.basicForm?.validateFields(['university']);
    },
    // 加载专业详情
    async loadMajorData() {
      const id = this.$route.query.id;
      if (!id) return;
      
      try {
        const major = await getMajorDetail({ id });
          // 设置基本信息
          this.basicInfo = {
            ...major,
            province_id: major.province_id,
            province_name: major.province_name,
            university: major.university_id
          };
          
          // 加载该省份的大学列表
          if (major.province_id) {
            await this.fetchUniversityList(major.province_id);
          }
          
          // 设置科目
          if (major.subjects && major.subjects.length > 0) {
            this.subjects = major.subjects.map(sub => ({
              id: sub.id,
                  code: sub.subject_code,
                  name: sub.subject_name,
                  credits: sub.credits,
                  type: sub.type, // === 1 ? 'required' : 'optional'
                  is_show: sub.is_show,
                  examSchedules: sub.examSchedules || [{ 
                    id: Date.now(),
                    month: '',
                    day: '',
                    time: '',
                    remark: ''
                  }]
                }));
              
        // 设置学校
        if (major.schools && major.schools.length > 0) {
          this.selectedSchools = major.schools.map(s => s.school_id);
          this.selectedSchoolsData = major.schools.map(school => ({
            key: school.school_id,
            name: school.school_name,
            isMajor: school.is_major === 1
          }));
        }
      }
      } catch (error) {
        console.error('加载专业详情失败:', error);
        message.error('加载专业详情失败');
      }
    },
    nextStep() {
      if (this.currentStep === 1) {
        // 验证步骤1表单
        // 确保表单已经渲染完成
        this.$nextTick(() => {
          console.log('表单实例:', this.form);
          this.form.validateFields((err, values) => {
            if (!err) {
              console.log('验证成功，表单值:', values);
              // 步骤切换前自动保存
              this.autoSave();
              this.currentStep++;
            } else {
              console.log('验证失败:', err);
              // 显示错误信息
              for (const key in err) {
                if (err.hasOwnProperty(key)) {
                  message.error(err[key].errors[0].message);
                }
              }
            }
          });
        });
      } else if (this.currentStep === 2) {
        // 验证步骤2表单
        if (this.subjects.length === 0) {
          message.error('请添加至少一个考试科目');
          return;
        }

        // 验证每个科目的必填字段
        let isValid = true;
        const errors = [];

        this.subjects.forEach((subject, index) => {
          if (!subject.code) {
            isValid = false;
            errors.push(`第${index + 1}个科目：课程代码不能为空`);
          }
          if (!subject.name) {
            isValid = false;
            errors.push(`第${index + 1}个科目：科目名称不能为空`);
          }
          if (!subject.credits || subject.credits <= 0) {
            isValid = false;
            errors.push(`第${index + 1}个科目：学分必须大于0`);
          }
          if (!subject.type) {
            isValid = false;
            errors.push(`第${index + 1}个科目：请选择类型`);
          }
          
          // 验证考期设置
          if (subject.examSchedules && subject.examSchedules.length > 0) {
            subject.examSchedules.forEach((schedule, sIndex) => {
              if (!schedule.month) {
                isValid = false;
                errors.push(`第${index + 1}个科目 第${sIndex + 1}个考期：请选择考期`);
              }
              if (!schedule.day) {
                isValid = false;
                errors.push(`第${index + 1}个科目 第${sIndex + 1}个考期：请选择周几`);
              }
              if (!schedule.time) {
                isValid = false;
                errors.push(`第${index + 1}个科目 第${sIndex + 1}个考期：请选择时间`);
              }
            });
          }
        });

        if (!isValid) {
          console.log('科目验证失败:', errors);
          errors.forEach(error => {
            message.error(error);
          });
          return;
        }

        // 步骤切换前自动保存
        this.autoSave();
        this.currentStep++;
        // 更新已选学校数据
        this.updateSelectedSchoolsData();
      }
    },
    prevStep() {
      // 步骤切换前自动保存
      this.autoSave();
      this.currentStep--;
    },
    addSubject() {
      const newSubject = {
        id: Date.now(),
        code: '',
        name: '',
        credits: 1,
        type: 'required',
        is_show: 1,
        examSchedules: [{ 
          id: Date.now(),
          month: '', // 一月、四月、七月、十月
          day: '', // 周六、周日
          time: '', // 上午、下午
          remark: ''
        }]
      };
      this.subjects = [...this.subjects, newSubject];
    },
    
    handleSubjectChange(id, field, value) {
      this.subjects = this.subjects.map(item => {
        if (item.id === id) {
          return { ...item, [field]: value };
        }
        return item;
      });
    },
    
    // 处理考期设置变更
    handleScheduleChange(subjectId, scheduleIndex, field, value) {
      this.subjects = this.subjects.map(subject => {
        if (subject.id === subjectId) {
          const newSchedules = [...subject.examSchedules];
          newSchedules[scheduleIndex] = {
            ...newSchedules[scheduleIndex],
            [field]: value
          };
          return { ...subject, examSchedules: newSchedules };
        }
        return subject;
      });
    },
    
    // 添加考期
    addSchedule(subjectId) {
      this.subjects = this.subjects.map(subject => {
        if (subject.id === subjectId) {
          const newSchedule = {
            id: Date.now(),
            month: '',
            day: '',
            time: '',
            remark: ''
          };
          return { ...subject, examSchedules: [...subject.examSchedules, newSchedule] };
        }
        return subject;
      });
    },
    
    // 删除考期
    deleteSchedule(subjectId, scheduleIndex) {
      this.subjects = this.subjects.map(subject => {
        if (subject.id === subjectId && subject.examSchedules.length > 1) {
          const newSchedules = [...subject.examSchedules];
          newSchedules.splice(scheduleIndex, 1);
          return { ...subject, examSchedules: newSchedules };
        }
        return subject;
      });
    },
    deleteSubject(id) {
      this.subjects = this.subjects.filter(subject => subject.id !== id);
    },
    renderSchool(option) {
      // 支持多种可能的数据结构
      const displayName = option.title || option.name || option.university_name || option.key;
      return <span>{displayName}</span>;
    },
    handleSchoolChange(targetKeys) {
      this.selectedSchools = [...targetKeys];
      this.updateSelectedSchoolsData();
    },
    handleMajorChange(schoolKey, isMajor) {
      // 使用Vue 2推荐的方式更新数组中的对象，确保响应式更新
      const index = this.selectedSchoolsData.findIndex(school => school.key === schoolKey);
      console.log(index)
      if (index !== -1) {
        // 创建新对象以确保响应式更新
        this.$set(this.selectedSchoolsData, index, {
          ...this.selectedSchoolsData[index],
          isMajor: isMajor
        });
      }
    },
    removeSchool(schoolKey) {
      this.selectedSchools = this.selectedSchools.filter(key => key !== schoolKey);
      this.updateSelectedSchoolsData();
    },
    updateSelectedSchoolsData() {
      // 创建新的学校数据，确保每个学校都有正确的key和isMajor属性
      const newSchoolsData = [];
      
      this.selectedSchools.forEach(key => {
        const existingSchool = this.selectedSchoolsData.find(s => s.key === key);
        const schoolInfo = this.schools.find(s => s.key === key) || { key, title: '未知学校' };
        
        // 如果学校已存在，保留其isMajor状态，否则默认为false
        newSchoolsData.push({
          key: schoolInfo.key,
          name: schoolInfo.title || schoolInfo.name,
          isMajor: existingSchool ? existingSchool.isMajor : false
        });
      });
      
      // 使用Vue.set确保数组响应式更新
      this.$set(this, 'selectedSchoolsData', newSchoolsData);
    },
    // 手动验证表单数据
    validateFormManually() {
      let isValid = true;
      const errors = [];
      
      // 验证省份
      if (!this.basicInfo.province_id) {
        isValid = false;
        errors.push('请选择省份');
      }
      
      // 验证专业名称
      if (!this.basicInfo.name) {
        isValid = false;
        errors.push('请输入专业名称');
      }
      
      // 验证专业代码
      if (!this.basicInfo.code) {
        isValid = false;
        errors.push('请输入专业代码');
      }
      
      // 验证学分
      if (!this.basicInfo.credits || this.basicInfo.credits <= 0) {
        isValid = false;
        errors.push('学分必须大于0');
      }
      // 验证课程门数
      if (!this.basicInfo.course_count || this.basicInfo.course_count <= 0) {
        isValid = false;
        errors.push('课程门数必须大于0');
      }
      
      if (isValid) {
        this.currentStep++;
      } else {
        console.log('手动验证失败:', errors);
        // 显示错误信息
        errors.forEach(error => {
          message.error(error);
        });
      }
    },
    handleSubmit() {
      try {
        this.submitting = true;
        // 成功提交后清除临时数据
        this.clearTemporaryData();
        
        // 验证表单数据
        if (this.subjects.length === 0) {
          message.warning('请至少添加一个考试科目');
          return;
        }

        // 检查是否至少选择了一个主修学校
        const hasMajorSchool = this.selectedSchoolsData.some(school => school.isMajor);
        if (this.selectedSchoolsData.length > 0 && !hasMajorSchool) {
          message.warning('请至少设置一个主修学校');
          return;
        }

        // 整合所有数据
        const formData = {
          id: this.basicInfo.id || undefined, // 编辑时传入ID
          name: this.basicInfo.name,
          code: this.basicInfo.code,
          province_id: this.basicInfo.province_id,
          province_name: this.basicInfo.province_name,
          education: this.basicInfo.education,
          credits: Number(this.basicInfo.credits) || 0,
          course_count: Number(this.basicInfo.course_count) || 0,
          policy_html: this.basicInfo.policy_html || '',
          subjects: this.subjects.map(subject => ({
            id: subject.subjectId || undefined,
            code: subject.code,
            name: subject.name,
            credits: Number(subject.credits) || 1,
            type: subject.type === 'required' ? 1 : 2, // 1-必修 2-选修
            is_show: subject.is_show,
            examSchedules: subject.examSchedules || []
          })),
          schools: this.selectedSchoolsData.map(school => ({
            id: school.id || undefined,
            school_id: school.key,
            name: school.name,
            isMajor: school.isMajor || false
          }))
        };

        console.log('提交的数据:', formData);
        
        // 调用API提交数据
        addOrUpdateMajor(formData).then(res => {
          message.success(res.msg || '专业信息保存成功！');
          this.$router.push({ path: '/analysis/majorList' });
        }).catch(error => {
          message.error(error.message || '保存失败，请重试');
        });
        
      } catch (error) {
        console.error('保存失败:', error);
        message.error(error.message || '保存失败，请重试');
      } finally {
        this.submitting = false;
      }
    },
    submitForm() {
      // 保留原方法，防止其他地方调用出错
      this.handleSubmit();
    },
    
    // 在页面关闭前尝试临时保存
    handleBeforeUnload(event) {
      // 简单判断是否有未保存的数据且开启了临时保存
      const hasUnsavedData = this.basicInfo.name || this.subjects.length > 0 || this.selectedSchools.length > 0;
      if (hasUnsavedData && this.enableTemporarySave) {
        // 有些浏览器可能会忽略这个消息，但我们仍然尝试临时保存
        this.saveTemporaryData();
        
        // 这里的返回值在现代浏览器中可能被忽略，但为了兼容性仍然保留
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    },
    
    // 页面隐藏时也尝试临时保存
    handlePageHide() {
      const hasUnsavedData = this.basicInfo.name || this.subjects.length > 0 || this.selectedSchools.length > 0;
      if (hasUnsavedData && this.enableTemporarySave) {
        this.saveTemporaryData();
      }
    },
    
    // 自动保存触发函数
    autoSave() {
      if (this.enableTemporarySave) {
        this.saveTemporaryData();
      }
    }
  },
  
  watch: {
    selectedSchools(newVal) {
      this.updateSelectedSchoolsData();
      // 学校选择变化时触发自动保存
      this.autoSave();
    },
    // 监听基本信息变化
    'basicInfo.name': 'autoSave',
    'basicInfo.code': 'autoSave',
    'basicInfo.credits': 'autoSave',
    // 监听科目列表变化
    subjects: {
      handler: 'autoSave',
      deep: true
    }
  },
  
  mounted() {
    // 检查是否有临时保存的数据
    this.loadTemporaryData();
    
    if (this.$route.query.id) {
      this.loadMajorData();
    }
    
    // 添加页面关闭前的事件监听，提示用户保存数据
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('pagehide', this.handlePageHide);
  },
  
  // 组件销毁时移除事件监听
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('pagehide', this.handlePageHide);
  },
}
</script>

<style scoped>
.major-management-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 600px;
}

.steps-indicator {
  display: flex;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eaeef3;
}

.steps-indicator > div {
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
}

.steps-indicator > div:not(:last-child):after {
  content: '';
  flex: 1;
  height: 2px;
  background-color: #eaeef3;
  margin-left: 10px;
}

.steps-indicator span {
  display: inline-block;
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  border-radius: 50%;
  background-color: #eaeef3;
  margin-right: 8px;
}

.step-active span {
  background-color: #409eff;
  color: #fff;
}

.step-completed span {
  background-color: #67c23a;
  color: #fff;
}

.step-completed:not(:last-child):after {
  background-color: #67c23a;
}

.step-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.exam-schedule-item {
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  margin-bottom: 10px;
}

.exam-schedule-item + .exam-schedule-item {
  margin-top: 10px;
}

.step-buttons {
  margin-top: 30px;
  text-align: right;
}

.step-buttons button {
  margin-left: 10px;
}

.selected-schools {
  margin-top: 30px;
}
</style>